import { z } from "zod";

/** Financial instructions only: this schema never initiates a payment. */
export const PAYMENT_METHODS = ["credit-card", "ach"] as const;
export const PAYMENT_TERMS = ["Due On Receipt", "NET 15", "NET 30"] as const;

export function cardNumberValid(value: string) {
  if (!/^\d{13,19}$/.test(value) || /^(\d)\1+$/.test(value)) return false;
  const sum = [...value].reverse().reduce((total, digit, index) => {
    const n = Number(digit) * (index % 2 ? 2 : 1);
    return total + (n > 9 ? n - 9 : n);
  }, 0);
  return sum % 10 === 0;
}

export function paymentFieldErrors(data: Record<string, string>, now = new Date()) {
  const errors: Record<string, string> = {};
  if (!PAYMENT_METHODS.includes(data.method as typeof PAYMENT_METHODS[number])) errors.method = "Choose Credit Card or ACH.";
  if (!PAYMENT_TERMS.includes(data.terms as typeof PAYMENT_TERMS[number])) errors.terms = "Choose the requested financial terms.";
  if (data.method === "credit-card") {
    data.cardNumber = (data.cardNumber || "").replace(/[\s-]/g, "");
    if (!cardNumberValid(data.cardNumber)) errors.cardNumber = "Enter a valid card number.";
    const expiration = /^(0[1-9]|1[0-2])\/(20\d{2})$/.exec(data.expiration || "");
    if (!expiration || new Date(Date.UTC(Number(expiration[2]), Number(expiration[1]), 1)) <= now) errors.expiration = "Enter a current expiration date as MM/YYYY.";
    if (!/^\d{3,4}$/.test(data.cvv || "")) errors.cvv = "Enter the three- or four-digit card security code.";
    if (data.feeAcknowledgment !== "Yes") errors.feeAcknowledgment = "Acknowledge the credit-card fee note.";
  }
  if (data.method === "ach") {
    const routing = data.routingNumber || "";
    const checksum = [...routing].reduce((sum, n, index) => sum + Number(n) * [3, 7, 1][index % 3], 0);
    if (!/^\d{9}$/.test(routing) || /^0+$/.test(routing) || checksum % 10 !== 0) errors.routingNumber = "Enter a valid nine-digit routing number.";
    if (!/^\d{4,17}$/.test(data.accountNumber || "")) errors.accountNumber = "Enter a bank account number using 4–17 digits.";
  }
  // The approved single-page template uses the same printable character set as
  // its preview. Reject unsupported characters before persisting a submission.
  for (const [key, value] of Object.entries(data)) {
    if (value && !z.string().regex(/^[\x20-\x7E]+$/).safeParse(value).success) errors[key] = "Use standard English letters, numbers, and punctuation.";
  }
  return errors;
}
